import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useUpdateUserPreferences, useUserPreferences } from '@hooks/user/use-user-preferences';
import { SESSION_CLEARED_EVENT } from '@utils/session-events';
import { isPatchLogPreviewPage } from '@utils/patch-log-preview';
import { isApprovalLinkPage } from '@utils/approval-link-page';
import { readHasSession, useHasSession } from '@hooks/use-has-session';

/** Rem scale for the whole app. 1 = the 16px base every layout was drawn at. */
export type TextScale = 1 | 1.1 | 1.2;

export const TEXT_SCALES: TextScale[] = [1, 1.1, 1.2];
export const DEFAULT_TEXT_SCALE: TextScale = 1;

const STORAGE_KEY = 'jarvis-text-scale';
const BASE_FONT_PX = 16;

export const isTextScale = (value: unknown): value is TextScale => value === 1 || value === 1.1 || value === 1.2;

const resolveInitialScale = (): TextScale => {
  // The CMS preview shows what customers see by default, not the visitor's size.
  if (typeof window === 'undefined' || isPatchLogPreviewPage) return DEFAULT_TEXT_SCALE;
  const saved = Number(localStorage.getItem(STORAGE_KEY));
  return isTextScale(saved) ? saved : DEFAULT_TEXT_SCALE;
};

interface TextScaleContextType {
  scale: TextScale;
  setScale: (next: TextScale) => void;
}

const TextScaleContext = createContext<TextScaleContextType | undefined>(undefined);

/**
 * One number, applied once: `html { font-size }`. Tailwind utilities and
 * Mantine sizes are rem-based, so both follow it; Mantine's `theme.scale`
 * stays 1 (see App.tsx) and the sidebar widths are rem too (content.tsx), so
 * nothing multiplies by it a second time.
 *
 * Same persistence story as the language: localStorage for first paint, the
 * profile for the next device, adopted once per session so a refetch never
 * undoes a choice the user just made.
 */
export const TextScaleProvider = ({ children }: { children: ReactNode }) => {
  const [scale, setScaleState] = useState<TextScale>(resolveInitialScale);
  // State, not a read at render: an SPA sign-in must enable the query.
  const hasSession = useHasSession();
  const { data: preferences } = useUserPreferences({ enabled: hasSession && !isPatchLogPreviewPage && !isApprovalLinkPage });
  const savePreference = useUpdateUserPreferences();
  const adoptedFromProfile = useRef(false);

  useEffect(() => {
    const stored = preferences?.textScale;
    if (!isTextScale(stored) || adoptedFromProfile.current) return;
    adoptedFromProfile.current = true;
    setScaleState(stored);
  }, [preferences?.textScale]);

  useEffect(() => {
    // 100% is the default, so it is stored as "nothing": after a logout reset
    // the key stays gone rather than being written back.
    // On the CMS preview page the visitor's own setting stays as it is.
    if (!isPatchLogPreviewPage) {
      if (scale === 1) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, String(scale));
    }
    document.documentElement.style.fontSize = scale === 1 ? '' : `${BASE_FONT_PX * scale}px`;
    document.documentElement.setAttribute('data-text-scale', String(scale));
  }, [scale]);

  // Logout: back to 100%, and let the next user's profile be adopted.
  useEffect(() => {
    const onSessionCleared = () => {
      adoptedFromProfile.current = false;
      setScaleState(DEFAULT_TEXT_SCALE);
    };
    window.addEventListener(SESSION_CLEARED_EVENT, onSessionCleared);
    return () => window.removeEventListener(SESSION_CLEARED_EVENT, onSessionCleared);
  }, []);

  const setScale = useCallback(
    (next: TextScale) => {
      setScaleState(next);
      adoptedFromProfile.current = true;
      if (readHasSession()) {
        savePreference.mutate({ textScale: next });
      }
    },
    [savePreference]
  );

  const value = useMemo(() => ({ scale, setScale }), [scale, setScale]);

  return <TextScaleContext.Provider value={value}>{children}</TextScaleContext.Provider>;
};

export const useTextScale = (): TextScaleContextType => {
  const context = useContext(TextScaleContext);
  if (!context) {
    throw new Error('useTextScale must be used within TextScaleProvider');
  }
  return context;
};
